import { Button, Icon, Menu, MenuButton, MenuItemOption, MenuList, MenuOptionGroup } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiEye } from "react-icons/fi";
import { OrganizationRoleType } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../auth/useLoggedInWithOrgContextState";
import { useSub } from "../../subscription/useSub";
import { viewAsRoleOptions } from "../../subscription/viewAsRoleLabels";
import { setViewAsRole, useViewAsRole } from "../../subscription/viewAsRoleStore";

const OWN_ROLES = "own";

/**
 * Lets an owner/admin preview the app as a narrower role without reassigning anyone's roles. The
 * preview only filters the UI (useSub narrows its flags from the previewed role); the backend keeps
 * authorizing by the real roles, which is also why only owners/admins get the selector — for them a
 * preview can only ever narrow.
 */
export const ViewAsRoleSelector = () => {
  const { t } = useTranslation();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { isSupplier } = useSub();
  const viewAsRole = useViewAsRole();

  if (isSupplier || !selectedOrg.canEdit) return null;

  const options = viewAsRoleOptions.map((option) => ({ role: option.role, label: t(option.labelKey) }));
  const activeLabel = options.find((option) => option.role === viewAsRole)?.label;

  return (
    <Menu placement="top-start" matchWidth>
      <MenuButton
        as={Button}
        size="sm"
        w="full"
        mb="2"
        variant={activeLabel ? "solid" : "ghost"}
        colorScheme={activeLabel ? "teal" : undefined}
        leftIcon={<Icon as={FiEye} />}
        color={activeLabel ? undefined : "smMuted"}
        fontWeight="normal"
      >
        {activeLabel ? `${t("Viewing as")} ${activeLabel}` : t("View as role")}
      </MenuButton>
      <MenuList>
        <MenuOptionGroup
          type="radio"
          value={viewAsRole ?? OWN_ROLES}
          onChange={(value) => setViewAsRole(value === OWN_ROLES ? null : (value as OrganizationRoleType))}
        >
          <MenuItemOption value={OWN_ROLES}>{t("My own roles")}</MenuItemOption>
          {options.map((option) => (
            <MenuItemOption key={option.role} value={option.role}>
              {option.label}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
};
